import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import type {SxProps} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select, {type SelectChangeEvent} from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import {lighten} from '@mui/system/colorManipulator';
import {type FC, type ReactNode, useEffect, useState} from 'react';
import {primaryColor, secondaryColor} from '@/theme/theme';

// TYPES
export type StringOption = {
  uuid: string;
  name: string;
};

type Props = {
  editable: boolean;
  value?: string;
  options?: Array<StringOption>;
  required?: boolean;
  startAdornment?: ReactNode;
  onValueChange: (optionId?: string) => void;
};

const SelectEditableOnHover: FC<Props> = ({editable, value, options = [], onValueChange}) => {
  const [editing, setEditing] = useState(false);
  const [hover, setHover] = useState(false);
  const [newValue, setNewValue] = useState(value ?? '');

  useEffect(() => {
    setNewValue(value ?? '');
  }, [value]);

  const handleMouseEnter = () => editable && setHover(true);
  const handleMouseLeave = () => editable && setHover(false);

  const handleChange = (e: SelectChangeEvent) => {
    const value = e.target.value;
    setNewValue(value);
    onValueChange(value);
    setEditing(false);
  };

  const handleClose = () => {
    setEditing(false);
    setHover(false);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={styles(hover, editing).container}
    >
      <FormControl variant="outlined" sx={{width: '100%'}}>
        <Select
          disabled={!editable}
          value={newValue}
          displayEmpty
          variant="outlined"
          onClose={handleClose}
          onChange={handleChange}
          size="small"
          sx={styles(hover, editing).select}
        >
          {options.map(({uuid, name}) => (
            <MenuItem key={uuid} value={uuid}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <EditOutlinedIcon color="primary" sx={styles(hover, editing).icon} />
    </Stack>
  );
};

const styles = (hover: boolean, editing: boolean): Record<string, SxProps> => ({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    border: hover ? '1px solid #d3d3d3' : '1px solid transparent',
    backgroundColor: hover ? lighten(secondaryColor, 0.9) : 'white',
    transition: 'all 500ms ease-in-out',
    pr: 0.5,
  },
  select: {
    pl: 0.5,
    height: '34px',
    display: 'flex',
    fontWeight: 900,
    '& .MuiSelect-select': {
      padding: '8px 0px',
    },
    // Quitar todos los borders del outlined
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none !important',
    },
    // Agregar borderBottom solo cuando está editando
    ...(editing && {
      borderBottom: `2px solid ${primaryColor}`,
    }),
    // Control del icono del select
    '& .MuiSelect-icon': {
      opacity: editing ? 1 : 0,
      transition: 'opacity 200ms ease-in-out',
    },
  },
  icon: {
    opacity: hover ? 1 : 0,
    transition: 'opacity 500ms ease-in-out',
  },
});

export default SelectEditableOnHover;
